<template>
  <div class="flex mr-4" role="tablist" aria-orientation="horizontal">
    <div v-for="(tab, tabIndex) in tabs" :key="tab.title || tabIndex" class="tab-container">
      <button
        v-if="!showSelectForTab[tabIndex]"
        :key="tab.title"
        class="p-tab"
        :class="{ 'p-tab-active': activeTabsMap[tabIndex] !== undefined }"
        @click="() => onTabClick(tabIndex)"
        @dblclick="() => onTabDoubleClick(tabIndex, tab)"
      >
        <span>
          {{ getTabLabel(tab, tabIndex) }}
          <span
            v-if="tab.options.length > 1 && activeTabsMap[tabIndex] !== undefined"
            class="text-xs count ml-2"
          >
            ({{ (activeTabsMap[tabIndex] ?? 0) + 1 }}/{{ tab.options.length }})
          </span>
        </span>
        <button
          v-if="activeTabsMap[tabIndex] !== undefined"
          class="close-btn"
          @click.stop="onTabDeactivate(tabIndex)"
        >
          ×
        </button>
      </button>
      <div v-if="showSelectForTab[tabIndex]" class="selectWrapper" @click.stop>
        <Select
          :modelValue="activeTabsMap[tabIndex]"
          :options="tab.options.map((opt, i) => ({ label: opt.title, value: i }))"
          optionLabel="label"
          optionValue="value"
          class="prefilter-select"
          autoFocus
          @change="(event) => onSelectChange(tabIndex, event.value)"
          @blur="() => (showSelectForTab[tabIndex] = false)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import Select from 'primevue/select';
import { useI18n } from 'vue-i18n';

import { type ActiveTabsMap, useTablePrefilterStore } from '@/stores/tablePrefilter';

const { t } = useI18n();
const prefilterStore = useTablePrefilterStore();

const MAX_CYCLE_OPTIONS = 1;

export interface PaginatedPrefilterOption {
  title: string;
  key: string;
  focusedColumn?: string;
}

export interface PaginatedPrefilterTab {
  title: string;
  options: PaginatedPrefilterOption[];
}

interface DataTableFilterProps {
  tabs: PaginatedPrefilterTab[];
  dataKey: string;
}

const props = defineProps<DataTableFilterProps>();
const emit = defineEmits<{
  (e: 'change', payload: PaginatedPrefilterOption[]): void;
}>();

// activeTabsMap: { tabIndex: selectedOptionIndex, ... }
// Example: { 0: 0, 1: 2 } means tab 0 is active with option 0, tab 1 is active with option 2
const activeTabsMap = ref<ActiveTabsMap>({});
const showSelectForTab = ref<Record<number, boolean>>({});

// Check if "All" tab (tab 0) is the only one with focusedColumn undefined
const allTabIndex = computed(() => {
  return props.tabs.findIndex((tab) => !tab.options.some((opt) => opt.focusedColumn));
});

function initializeActiveStates() {
  if (!props.tabs || props.tabs.length === 0) return;

  const savedActiveTabsMap = prefilterStore.getActiveTabsMap(props.dataKey);

  // Validate saved map: ensure tabIndex and selectedOptionIndex are within bounds
  const validatedMap: ActiveTabsMap = {};
  for (const [tabIndexStr, selectedOptionIndex] of Object.entries(savedActiveTabsMap)) {
    const tabIndex = Number(tabIndexStr);
    const tab = props.tabs[tabIndex];

    if (tab && selectedOptionIndex >= 0 && selectedOptionIndex < tab.options.length) {
      validatedMap[tabIndex] = selectedOptionIndex;
    }
  }

  activeTabsMap.value = validatedMap;

  void nextTick(() => {
    emitActiveOptions();
  });
}

function getTabLabel(tab: PaginatedPrefilterTab, tabIndex: number): string {
  const selectedOptionIndex = activeTabsMap.value[tabIndex];

  // If tab not active, just show title
  if (selectedOptionIndex === undefined) {
    return t(tab.title);
  }

  const activeOption = tab.options[selectedOptionIndex];
  if (!activeOption) {
    return t(tab.title);
  }

  // If single option, just show title
  if (tab.options.length === 1) {
    return t(tab.title);
  }

  // Multiple options: show "Title: OptionName"
  return `${t(tab.title)}: ${activeOption.title}`;
}

function onTabClick(tabIndex: number) {
  const tab = props.tabs[tabIndex];
  if (!tab) return;

  const isAllTab = tabIndex === allTabIndex.value;

  // If clicking "All" tab
  if (isAllTab) {
    // "All" is exclusive - clicking it clears all other tabs
    activeTabsMap.value = { [tabIndex]: 0 };
  } else {
    // Clicking a non-"All" tab
    // If "All" is active, deactivate it first
    if (activeTabsMap.value[allTabIndex.value] !== undefined) {
      delete activeTabsMap.value[allTabIndex.value];
    }

    // Purely additive: always activate the clicked tab, never deactivate
    // Tab stays active once selected (deactivation via double-click)
    if (activeTabsMap.value[tabIndex] === undefined) {
      activeTabsMap.value[tabIndex] = 0;
    }
  }

  prefilterStore.setActiveTabsMap(props.dataKey, activeTabsMap.value);

  emitActiveOptions();
}

function onTabDoubleClick(tabIndex: number, tab?: PaginatedPrefilterTab) {
  const currentTab = tab ?? props.tabs[tabIndex];
  if (!currentTab) return;

  // Show dropdown if more than one option
  if (currentTab.options.length > MAX_CYCLE_OPTIONS) {
    showSelectForTab.value = { ...showSelectForTab.value, [tabIndex]: true };
  }
}

function onTabDeactivate(tabIndex: number) {
  // Remove the tab from active set via close button click
  delete activeTabsMap.value[tabIndex];

  prefilterStore.setActiveTabsMap(props.dataKey, activeTabsMap.value);

  emitActiveOptions();
}

function onSelectChange(tabIndex: number, optionIndex: number) {
  activeTabsMap.value[tabIndex] = optionIndex;
  showSelectForTab.value[tabIndex] = false;

  prefilterStore.setActiveTabsMap(props.dataKey, activeTabsMap.value);

  emitActiveOptions();
}

function emitActiveOptions() {
  // Collect all active options in order
  const activeOptions: PaginatedPrefilterOption[] = [];

  for (const tabIndex of Object.keys(activeTabsMap.value)
    .map(Number)
    .sort((a, b) => a - b)) {
    const tab = props.tabs[tabIndex];
    if (!tab) continue;

    const selectedOptionIndex = activeTabsMap.value[tabIndex] ?? 0;
    const option = tab.options[selectedOptionIndex];

    if (option) {
      activeOptions.push(option);
    }
  }

  emit('change', activeOptions);
}

// Close select on outside click
function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  // Check if click is outside select wrapper
  if (!target.closest('.selectWrapper') && !target.closest('.p-select-overlay')) {
    showSelectForTab.value = {};
  }
}

onMounted(() => {
  initializeActiveStates();
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

watch(() => props.tabs, initializeActiveStates, { immediate: true });
</script>

<style scoped lang="css">
@layer app {
  .tab-container {
    position: relative;
  }

  .p-tab {
    position: relative;
    padding: 0.6rem 1rem;
    font-weight: 600;
    border: none;
    border-bottom: 3px solid transparent;
    background: none;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .p-tab:hover {
    color: theme('colors.interactionblue.500');
  }

  .p-tab-active {
    border-bottom-color: theme('colors.interactionblue.500');
    color: theme('colors.interactionblue.500');
  }

  .count {
    color: var(--text-secondary);
  }

  .close-btn {
    margin-left: 0.6rem;
    font-size: 1rem;
    cursor: pointer;
    opacity: 0.7;
  }

  .close-btn:hover {
    opacity: 1;
    color: theme('colors.red.500');
  }

  .selectWrapper {
    position: absolute;
    overflow: visible;
    z-index: 999;
    bottom: -10px;
    height: auto;
  }

  .prefilter-select {
    position: relative;
    width: 220px;
  }
}
</style>
